<!-- 平台管理端 — 商家类目申请审核
  根据 B2B2C 方案 ADR-006：审核通过时需设置佣金率，默认填入类目默认佣金率，审核人可手动调整 -->
<template>
  <!-- 搜索栏 -->
  <ContentWrap>
    <el-form :inline="true" :model="queryParams" class="-mb-15px">
      <el-form-item label="申请状态">
        <el-select v-model="queryParams.applyStatus" clearable placeholder="全部" class="!w-140px">
          <el-option label="待审核" :value="0" />
          <el-option label="已通过" :value="1" />
          <el-option label="已驳回" :value="2" />
        </el-select>
      </el-form-item>
      <el-form-item label="店铺ID">
        <el-input
          v-model="queryParams.shopId"
          placeholder="输入店铺ID"
          clearable
          class="!w-160px"
          @keyup.enter="getList"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="getList">
          <Icon class="mr-5px" icon="ep:search" />搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />重置
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap class="mt-16px">
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="申请ID" width="80" prop="id" />
      <el-table-column label="店铺ID" width="80" prop="shopId" />
      <el-table-column label="申请类目" min-width="200" prop="categoryName" show-overflow-tooltip />
      <el-table-column label="父级类目" min-width="150" prop="parentCategoryName" show-overflow-tooltip />
      <el-table-column label="申请状态" width="100" align="center">
        <template #default="{ row }">
          <el-tag :type="statusTagType(row.applyStatus)">{{ statusLabel(row.applyStatus) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="驳回原因" min-width="160" prop="rejectReason" show-overflow-tooltip>
        <template #default="{ row }">
          <span v-if="row.rejectReason" class="text-red-500">{{ row.rejectReason }}</span>
          <span v-else class="text-gray-400">-</span>
        </template>
      </el-table-column>
      <el-table-column
        label="申请时间"
        width="170"
        prop="createTime"
        :formatter="dateFormatter"
      />
      <el-table-column label="审核时间" width="170">
        <template #default="{ row }">
          <span v-if="row.auditTime">{{ dateFormatter(row, null, row.auditTime, null) }}</span>
          <span v-else class="text-gray-400">-</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <template v-if="row.applyStatus === 0">
            <el-button type="success" link @click="handleApprove(row)">通过</el-button>
            <el-button type="danger" link @click="handleReject(row)">驳回</el-button>
          </template>
          <span v-else class="text-gray-400">-</span>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 审核通过弹窗（含佣金率设置） — ADR-006：审核界面默认填入类目默认佣金率，审核人可手动调整 -->
  <el-dialog v-model="approveDialogVisible" title="审核通过 — 设置佣金率" width="480px">
    <el-form label-width="100px">
      <el-form-item label="店铺ID">
        <span>{{ currentRow?.shopId }}</span>
      </el-form-item>
      <el-form-item label="申请类目">
        <span>{{ currentRow?.categoryName }}</span>
      </el-form-item>
      <el-form-item v-if="currentRow?.parentCategoryName" label="父级类目">
        <span>{{ currentRow.parentCategoryName }}</span>
      </el-form-item>
      <el-form-item label="佣金率(%)" required>
        <el-input-number
          v-model="approveForm.commissionRate"
          :min="0"
          :max="100"
          :precision="2"
          :step="0.5"
          class="!w-200px"
          placeholder="如 8.00"
        />
        <span class="ml-8px text-gray-400 text-12px">填 0 表示免佣金</span>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="approveDialogVisible = false">取消</el-button>
      <el-button
        type="success"
        :loading="auditLoading"
        :disabled="approveForm.commissionRate === undefined || approveForm.commissionRate === null"
        @click="confirmApprove"
      >
        确认通过
      </el-button>
    </template>
  </el-dialog>

  <!-- 驳回原因弹窗 -->
  <el-dialog v-model="rejectDialogVisible" title="驳回申请" width="450px">
    <el-form label-width="80px">
      <el-form-item label="申请类目">
        <span>{{ currentRow?.categoryName }}</span>
      </el-form-item>
      <el-form-item label="驳回原因" required>
        <el-input
          v-model="rejectForm.reason"
          type="textarea"
          :rows="3"
          placeholder="请输入驳回原因（必填）"
          maxlength="200"
          show-word-limit
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="rejectDialogVisible = false">取消</el-button>
      <el-button
        type="danger"
        :loading="auditLoading"
        :disabled="!rejectForm.reason.trim()"
        @click="confirmReject"
      >
        确认驳回
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { dateFormatter } from '@/utils/formatTime';
import { getCategoryApplyPage, auditCategoryApply } from '@/api/merchant/category-apply-admin';
import { ContentWrap } from '@/components/ContentWrap';

defineOptions({ name: 'CategoryApplyReview' });

const loading = ref(false);
const list = ref([]);
const total = ref(0);
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  applyStatus: undefined as number | undefined,
  shopId: undefined as number | undefined,
});

function statusLabel(status: number) {
  const map: Record<number, string> = { 0: '待审核', 1: '已通过', 2: '已驳回' };
  return map[status] ?? '未知';
}

function statusTagType(status: number) {
  const map: Record<number, string> = { 0: 'warning', 1: 'success', 2: 'danger' };
  return map[status] ?? 'info';
}

async function getList() {
  loading.value = true;
  try {
    const data = await getCategoryApplyPage(queryParams);
    list.value = data?.list ?? [];
    total.value = data?.total ?? 0;
  } finally {
    loading.value = false;
  }
}

function resetQuery() {
  queryParams.applyStatus = undefined;
  queryParams.shopId = undefined;
  queryParams.pageNo = 1;
  getList();
}

// ========== 审核通过（弹窗设置佣金率）— ADR-006 ==========
const approveDialogVisible = ref(false);
const auditLoading = ref(false);
const currentRow = ref<any>(null);
const approveForm = reactive({ commissionRate: undefined as number | undefined });

function handleApprove(row: any) {
  currentRow.value = row;
  // 默认佣金率从 product_category 获取（TODO: 后续联 product_category API 预填）
  approveForm.commissionRate = undefined;
  approveDialogVisible.value = true;
}

async function confirmApprove() {
  if (approveForm.commissionRate === undefined || approveForm.commissionRate === null) {
    ElMessage.warning('请设置佣金率');
    return;
  }
  auditLoading.value = true;
  try {
    await auditCategoryApply({
      applyId: currentRow.value.id,
      approved: true,
      commissionRate: approveForm.commissionRate,
    });
    ElMessage.success(`已通过，佣金率设为 ${approveForm.commissionRate}%`);
    approveDialogVisible.value = false;
    getList();
  } finally {
    auditLoading.value = false;
  }
}

// ========== 驳回审核（弹窗填写原因）==========
const rejectDialogVisible = ref(false);
const rejectForm = reactive({ reason: '' });

function handleReject(row: any) {
  currentRow.value = row;
  rejectForm.reason = '';
  rejectDialogVisible.value = true;
}

async function confirmReject() {
  if (!rejectForm.reason.trim()) {
    ElMessage.warning('请填写驳回原因');
    return;
  }
  auditLoading.value = true;
  try {
    await auditCategoryApply({
      applyId: currentRow.value.id,
      approved: false,
      reason: rejectForm.reason,
    });
    ElMessage.success('已驳回');
    rejectDialogVisible.value = false;
    getList();
  } finally {
    auditLoading.value = false;
  }
}

onMounted(() => {
  getList();
});
</script>
